/**
 * Le panneau d'un Move, écran par écran. PUR : aucune dépendance à React, tout se teste.
 *
 *   étagère  →  déblocage  →  appliquer
 *
 * « étagère » : la trousse est ouverte, on choisit CE DONT ON A BESOIN parmi des Moves voisins.
 * « déblocage » : on reconnaît la bonne écriture parmi les erreurs classiques. Un mauvais choix ne
 * coûte rien et n'est jamais « faux » tout court : il dit pourquoi, et on réessaie — c'est là qu'on
 * apprend. Un Move déjà débloqué saute cet écran : on ne redemande pas ce qui est acquis.
 * « appliquer » : le savoir pose sa ligne sur la feuille, le geste ouvre sa famille de touches.
 */

import { moveById, type MoveDef, type MoveShelf } from './move-catalog';

export type MovePanelState =
  | { step: 'shelf'; wrong?: string }
  | { step: 'unlock'; moveId: string; wrong?: string }
  | { step: 'apply'; moveId: string; justUnlocked: boolean };

/** Les Moves de l'étagère, dans l'ordre d'affichage : le bon n'est jamais le premier par défaut. */
export function shelfMoves(shelf: MoveShelf): MoveDef[] {
  const ids = [...shelf.alsoIds];
  // le bon Move est glissé à une place stable mais pas en tête (déterminé par son id, pas au hasard)
  const at = shelf.moveId.length % (ids.length + 1);
  ids.splice(at, 0, shelf.moveId);
  return ids.map(moveById).filter((m): m is MoveDef => m !== null);
}

export function openPanel(shelf: MoveShelf, unlocked: ReadonlySet<string>): MovePanelState {
  // la trousse s'ouvre sur son étagère, même si le Move est déjà acquis : c'est à l'élève de choisir
  void shelf;
  void unlocked;
  return { step: 'shelf' };
}

/** On touche un Move de l'étagère. */
export function pickMove(
  shelf: MoveShelf,
  id: string,
  unlocked: ReadonlySet<string>,
): MovePanelState {
  if (id !== shelf.moveId) {
    const m = moveById(id);
    return {
      step: 'shelf',
      wrong: m ? `« ${m.name} » ne sert pas ici.` : 'Ce n’est pas ce qu’il te faut ici.',
    };
  }
  if (unlocked.has(id)) return { step: 'apply', moveId: id, justUnlocked: false };
  /*
   * ⚠️ Un GESTE est fourni avec la trousse (retour de Philippe du 4/11/2026 : « il est débloqué par
   * défaut ») : on ne le reconnaît pas parmi des écritures, on s'en sert. Seule une CONNAISSANCE se
   * gagne — là, l'écran de déblocage est le cœur de l'apprentissage.
   */
  const m = moveById(id);
  if (m?.kind === 'geste') return { step: 'apply', moveId: id, justUnlocked: true };
  return { step: 'unlock', moveId: id };
}

export interface PickWritingResult {
  state: MovePanelState;
  /** l'id du Move si ce choix vient de le débloquer */
  unlocked?: string;
}

/** On touche une écriture au déblocage. */
export function pickWriting(move: MoveDef, index: number): PickWritingResult {
  const c = move.choices[index];
  if (!c) return { state: { step: 'unlock', moveId: move.id } };
  if (!c.ok) return { state: { step: 'unlock', moveId: move.id, wrong: c.why } };
  return { state: { step: 'apply', moveId: move.id, justUnlocked: true }, unlocked: move.id };
}

/** Revenir à l'étagère depuis le déblocage. */
export function backToShelf(): MovePanelState {
  return { step: 'shelf' };
}

/** Le titre du panneau à chaque écran. PUR. */
export function panelTitle(state: MovePanelState, shelf: MoveShelf): string {
  if (state.step === 'shelf') return shelf.question;
  const m = moveById(state.moveId);
  if (!m) return shelf.question;
  if (state.step === 'unlock') return 'Laquelle est la bonne ?';
  return m.name;
}

/** Ce que dit le bouton qui applique le Move. PUR. */
export function applyLabel(move: MoveDef): string {
  return move.kind === 'savoir' ? 'Poser sur ma feuille' : 'Appliquer';
}
